import { type ReactNode, useRef, useState } from 'react';
import { useSegment } from '@repo/poly-hooks/segment';
import { IdNowError, type IdNowErrorKeys } from '../idnow/error';
import { FraudConfirmed, FraudSuspicion } from '../idnow/fraud';
import { StartedOnPhone } from '../idnow/started-on-phone';
import { browser, isMobile } from '../../utils/device';
import { WaitingPopup } from '../idnow/popup';
import { Iframe } from '../idnow/iframe';
import { DownloadApp } from './download-app';
import { LandingMobile } from './landing-mobile';
import { LandingDesktop } from './landing-desktop';
import { ConsentPopup } from './consent-popup';

export type { IdNowErrorKeys } from '../idnow/error';

export type IdNowStatus =
  | 'pending'
  | 'on_call'
  | 'waiting_results'
  | 'succeeded'
  | 'canceled'
  | 'rejected'
  | 'failed';

export function IDNow2({
  status,
  tryAgain,
  token,
  onSendSMS,
  reason,
  qrCodeUrl,
  hasGermanResidencePermit,
  onBack,
  onWaitingResults,
  onRejected,
  trackingPrefix,
}: {
  token: string;
  status: IdNowStatus;
  tryAgain: () => void;
  onSendSMS: () => void;
  qrCodeUrl: string;
  hasGermanResidencePermit: boolean;
  trackingPrefix: string;
  reason?: IdNowErrorKeys;
  onBack?: () => void;
  onWaitingResults?: () => void;
  onRejected?: () => void;
}): ReactNode {
  const [state, setState] = useState<'landing' | 'consent' | 'download-app' | 'video-call'>(
    'landing'
  );
  const segment = useSegment();
  const trackedStatus = useRef<string | null>(null);

  const src = `${process.env.IDNOW_IFRAME_DOMAIN}/web/qontovideo/identifications/${token}/language-selection`;

  // Shared statuses
  if (status === 'canceled' || status === 'failed') {
    return (
      <IdNowError
        onSwitchToPhone={() => {
          segment.track(`${trackingPrefix}_idnow_error_device_switched`);
          setState('download-app');
          tryAgain();
        }}
        onTryAgain={() => {
          segment.track(`${trackingPrefix}_idnow_error_retry_started`);
          tryAgain();
        }}
        reason={reason}
      />
    );
  }
  if (status === 'waiting_results') {
    if (trackedStatus.current !== 'waiting_results') {
      trackedStatus.current = 'waiting_results';
      onWaitingResults?.();
    }
    return <FraudSuspicion />;
  }
  if (status === 'rejected') {
    if (trackedStatus.current !== 'rejected') {
      trackedStatus.current = 'rejected';
      onRejected?.();
    }
    return <FraudConfirmed />;
  }

  if (isMobile) {
    return (
      <LandingMobile
        hasGermanResidencePermit={hasGermanResidencePermit}
        onContinue={link => {
          segment.track(`${trackingPrefix}_idnow_download_link_clicked`, { link });
        }}
        token={token}
        trackingPrefix={trackingPrefix}
      />
    );
  }

  if (status === 'on_call') {
    return <StartedOnPhone />;
  }
  if (status === 'succeeded') {
    return null;
  }
  if (state === 'video-call' && browser?.name === 'Safari') {
    return (
      <WaitingPopup
        goBack={() => {
          setState('landing');
        }}
        src={src}
      />
    );
  }
  if (state === 'video-call') {
    return <Iframe src={src} />;
  }
  if (state === 'download-app') {
    return (
      <DownloadApp
        onBack={() => {
          setState('landing');
        }}
        onSendSms={onSendSMS}
        qrCodeUrl={qrCodeUrl}
        token={token}
      />
    );
  }
  return (
    <>
      <LandingDesktop
        hasGermanResidencePermit={hasGermanResidencePermit}
        onBack={onBack}
        onContinueComputer={() => {
          setState('consent');
        }}
        onContinuePhone={() => {
          segment.track(`${trackingPrefix}_idnow_device_switched`);
          setState('download-app');
        }}
        trackingPrefix={trackingPrefix}
      />

      <ConsentPopup
        isOpen={state === 'consent'}
        onCancel={() => {
          setState('landing');
        }}
        onContinue={() => {
          segment.track(`${trackingPrefix}_idnow_call_started`);
          setState('video-call');
        }}
      />
    </>
  );
}
